import { useRef, useState } from 'react'
import { Calendar, ChevronDown, Search, X } from 'lucide-react'
import { useGlobalFilters, PERIOD_LABELS, type PeriodOption } from '../../contexts/GlobalFiltersContext'
import { useCustomers } from '../../hooks/useCustomers'
import { useTenantStore } from '../../stores/tenantStore'

interface GlobalFiltersBarProps {
  isOpen: boolean
  onClose: () => void
}

const PERIOD_OPTIONS: PeriodOption[] = [
  'questo_mese',
  'trimestre',
  'ytd',
  'anno_scorso',
  'custom',
]

export function GlobalFiltersBar({ isOpen, onClose }: GlobalFiltersBarProps) {
  const { filters, setFilter, clearFilters, hasActiveFilters } = useGlobalFilters()
  const { data: customers = [] } = useCustomers()
  const serviceLines = useTenantStore(s => s.settings?.serviceLines ?? ['SG', 'LIC', 'DEV', 'RIV', 'PS'])

  const [customerSearch, setCustomerSearch] = useState(filters.customer ?? '')
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const customerInputRef = useRef<HTMLInputElement>(null)

  const filteredCustomers = customers
    .filter(c => c.customer_name.toLowerCase().includes(customerSearch.toLowerCase()))
    .slice(0, 8)

  const handleClearAll = () => {
    setCustomerSearch('')
    clearFilters()
    onClose()
  }

  const handleRemovePeriod = () => setFilter('period', undefined)
  const handleRemoveCustomer = () => {
    setCustomerSearch('')
    setFilter('customer', undefined)
  }
  const handleRemoveServiceLine = () => setFilter('service_line', undefined)

  const handleCustomerSelect = (name: string) => {
    setCustomerSearch(name)
    setFilter('customer', name)
    setShowCustomerDropdown(false)
  }

  const handleCustomerInputChange = (v: string) => {
    setCustomerSearch(v)
    setShowCustomerDropdown(true)
    if (!v) setFilter('customer', undefined)
  }

  const handleCustomerBlur = () => {
    // Breve ritardo per permettere il click sulla voce del dropdown
    setTimeout(() => setShowCustomerDropdown(false), 150)
  }

  if (!isOpen && !hasActiveFilters) return null

  return (
    <div className="border-b border-white/[0.06] bg-[--km-surface-1]/80 backdrop-blur-sm px-4 md:px-6 py-2.5 animate-fadeInUp">
      <div className="max-w-[1400px] mx-auto flex flex-wrap items-center gap-2">

        {/* ── Filtro periodo ────────────────────────────────────── */}
        <div className="relative">
          <label className="sr-only">Periodo</label>
          <div className="flex items-center gap-1 rounded-lg border border-white/[0.08] bg-[--km-surface-2] px-2.5 py-1 text-xs">
            <Calendar size={11} className="shrink-0 text-[--km-text-muted]" />
            <select
              value={filters.period ?? ''}
              onChange={e => {
                const v = e.target.value as PeriodOption | ''
                setFilter('period', v || undefined)
              }}
              className="bg-transparent text-[--km-text-primary] cursor-pointer outline-none pr-4 appearance-none"
              style={{ minWidth: 110 }}
            >
              <option value="">Periodo</option>
              {PERIOD_OPTIONS.map(p => (
                <option key={p} value={p}>{PERIOD_LABELS[p]}</option>
              ))}
            </select>
            <ChevronDown size={10} className="shrink-0 text-[--km-text-muted] pointer-events-none" />
          </div>

          {/* Date range custom */}
          {filters.period === 'custom' && (
            <div className="absolute top-full left-0 mt-1 z-50 flex items-center gap-1.5 rounded-lg border border-white/[0.1] bg-[--km-surface-3] p-2 shadow-xl">
              <input
                type="date"
                value={filters.date_from ?? ''}
                onChange={e => setFilter('date_from', e.target.value || undefined)}
                className="rounded-md border border-white/[0.08] bg-[--km-surface-2] px-2 py-1 text-xs text-[--km-text-primary] outline-none focus:border-indigo-500/50"
              />
              <span className="text-[10px] text-[--km-text-muted]">→</span>
              <input
                type="date"
                value={filters.date_to ?? ''}
                onChange={e => setFilter('date_to', e.target.value || undefined)}
                className="rounded-md border border-white/[0.08] bg-[--km-surface-2] px-2 py-1 text-xs text-[--km-text-primary] outline-none focus:border-indigo-500/50"
              />
            </div>
          )}
        </div>

        {/* ── Filtro cliente ────────────────────────────────────── */}
        <div className="relative">
          <div className="flex items-center gap-1 rounded-lg border border-white/[0.08] bg-[--km-surface-2] px-2.5 py-1 text-xs">
            <Search size={11} className="shrink-0 text-[--km-text-muted]" />
            <input
              ref={customerInputRef}
              type="text"
              placeholder="Cliente"
              value={customerSearch}
              onChange={e => handleCustomerInputChange(e.target.value)}
              onFocus={() => setShowCustomerDropdown(true)}
              onBlur={handleCustomerBlur}
              className="w-28 bg-transparent text-[--km-text-primary] placeholder:text-[--km-text-muted] outline-none"
            />
            {customerSearch && (
              <button onClick={handleRemoveCustomer} className="text-[--km-text-muted] hover:text-[--km-text-primary]">
                <X size={10} />
              </button>
            )}
          </div>

          {/* Autocomplete dropdown */}
          {showCustomerDropdown && filteredCustomers.length > 0 && (
            <ul className="absolute top-full left-0 mt-1 z-50 w-56 rounded-lg border border-white/[0.1] bg-[--km-surface-3] py-1 shadow-xl">
              {filteredCustomers.map(c => (
                <li key={c.customer_name}>
                  <button
                    className="w-full px-3 py-1.5 text-left text-xs text-[--km-text-primary] hover:bg-white/[0.05] transition-colors"
                    onMouseDown={() => handleCustomerSelect(c.customer_name)}
                  >
                    {c.customer_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Filtro service line ───────────────────────────────── */}
        <div className="flex items-center gap-1 rounded-lg border border-white/[0.08] bg-[--km-surface-2] px-2.5 py-1 text-xs">
          <select
            value={filters.service_line ?? ''}
            onChange={e => setFilter('service_line', e.target.value || undefined)}
            className="bg-transparent text-[--km-text-primary] cursor-pointer outline-none pr-4 appearance-none"
            style={{ minWidth: 110 }}
          >
            <option value="">Service line</option>
            {serviceLines.map(sl => (
              <option key={sl} value={sl}>{sl}</option>
            ))}
          </select>
          <ChevronDown size={10} className="shrink-0 text-[--km-text-muted] pointer-events-none" />
        </div>

        {/* ── Filtri attivi (chips) ─────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-1.5 ml-1">
          {filters.period && (
            <FilterChip
              label={PERIOD_LABELS[filters.period]}
              onRemove={handleRemovePeriod}
            />
          )}
          {filters.customer && (
            <FilterChip label={filters.customer} onRemove={handleRemoveCustomer} />
          )}
          {filters.service_line && (
            <FilterChip label={filters.service_line} onRemove={handleRemoveServiceLine} />
          )}
        </div>

        {/* ── Azzera filtri ─────────────────────────────────────── */}
        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="ml-auto flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-[--km-text-muted] hover:text-[--km-text-primary] hover:bg-white/[0.04] transition-colors"
          >
            <X size={11} />
            Azzera filtri
          </button>
        )}
      </div>
    </div>
  )
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/15 px-2 py-0.5 text-[10px] font-medium text-indigo-300 ring-1 ring-indigo-500/25">
      {label}
      <button
        onClick={onRemove}
        className="hover:text-white transition-colors"
        aria-label={`Rimuovi filtro ${label}`}
      >
        <X size={9} />
      </button>
    </span>
  )
}
